import type { GridColDef, GridValidRowModel } from '@mui/x-data-grid';
import { download, escapeCsvValue } from '../files';
import { formatDateToLocale, isValidDate } from '../dates';

type CellValue = string | number | undefined;
type ColumnValueGetter = (
    value: CellValue,
    row: GridValidRowModel,
) => CellValue;
type ColumnValueFormatter = (value: CellValue) => CellValue;

const getCellValue = (
    column: GridColDef,
    row: GridValidRowModel,
): CellValue => {
    const value = column.valueGetter
        ? (column.valueGetter as ColumnValueGetter)(row[column.field], row)
        : (row[column.field] ?? '');

    return column.valueFormatter
        ? (column.valueFormatter as ColumnValueFormatter)(value)
        : value;
};

const getHeader = (column: GridColDef): string =>
    isValidDate(column.field)
        ? formatDateToLocale(column.field)
        : (column.headerName ?? column.field);

export const getDelimitedDataRows = (
    columns: GridColDef[],
    rows: GridValidRowModel[],
    isCsv = false,
    numberOfColumnsToSkip = 0,
): string[] => {
    const columnsToUse = numberOfColumnsToSkip
        ? columns.slice(numberOfColumnsToSkip)
        : columns;

    return rows.map((row) =>
        columnsToUse
            .map((column) => {
                const value = getCellValue(column, row);

                return isCsv ? escapeCsvValue(value) : value;
            })
            .join(isCsv ? ',' : '\t'),
    );
};

export const copyDataGridToClipboard = (
    columns: GridColDef[],
    rows: GridValidRowModel[],
    numberOfColumnsToSkip = 0,
    callback: () => void,
): void => {
    const dataRows = getDelimitedDataRows(
        columns,
        rows,
        false,
        numberOfColumnsToSkip,
    );

    const tsv = dataRows.join('\n');

    navigator.clipboard
        .writeText(tsv)
        .then(callback)
        .catch((err) => console.error('Failed to copy: ', err));
};

export const downloadDataGridCsv = (
    columns: GridColDef[],
    rows: GridValidRowModel[],
    fileName: string,
): void => {
    const headers = columns.map(getHeader);
    const dataRows = getDelimitedDataRows(columns, rows, true);

    const csv = ['sep=,', headers.join(','), ...dataRows].join('\n');

    const csvBlob = new Blob([csv], {
        type: 'text/csv;charset=utf-8;',
    });

    download(csvBlob, fileName, '.csv');
};
